import { jsx, jsxs } from 'react/jsx-runtime';
import { faCaretDown, faCaretUp } from '@optimizely/pro-solid-svg-icons';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Button from '../../Button/index.js';
import { Icon } from '../../Icon/icon.js';

const SORT_ORDERS = {
  ASC: "asc",
  DESC: "desc"
};
const SORT_ORDER_DIRECTION = {
  [SORT_ORDERS.ASC]: "up",
  [SORT_ORDERS.DESC]: "down"
};
const TH = ({
  children,
  className,
  colSpan,
  isCollapsed,
  isNumerical,
  minWidth,
  sorting,
  testSection,
  textAlign,
  width,
  ...props
}) => {
  const classes = classNames(
    {
      "oui-cell-collapse": isCollapsed,
      "oui-numerical": isNumerical
    },
    className
  );
  const styles = {
    minWidth,
    textAlign,
    width
  };
  const sortingIcon = useMemo(() => {
    const sortClassNames = classNames({
      "align-self-end": true,
      "oui-table__sort-icon": true,
      "oui-table__sort-icon--inactive": sorting.isActiveColumn === false
    });
    let sortDirectionIcon;
    switch (SORT_ORDER_DIRECTION[sorting.order || ""]) {
      case "up":
        sortDirectionIcon = faCaretUp;
        break;
      case "down":
        sortDirectionIcon = faCaretDown;
        break;
    }
    if (typeof sorting.order === "string" && Object.values(SORT_ORDERS).includes(sorting.order)) {
      return /* @__PURE__ */ jsx(
        Icon,
        {
          className: sortClassNames,
          icon: sortDirectionIcon,
          size: "small"
        }
      );
    }
    return null;
  }, [sorting.order, sorting.isActiveColumn]);
  const tableHeaderContent = sorting.canSort ? /* @__PURE__ */ jsxs(
    Button,
    {
      className: "oui-table__sort-btn flex--inline flex-align--center wrap-text",
      onClick: sorting.handleSort,
      style: "unstyled",
      testSection: "table-header-sort-button",
      children: [
        children,
        sortingIcon
      ]
    }
  ) : children;
  return /* @__PURE__ */ jsx(
    "th",
    {
      className: classes,
      colSpan,
      "data-test-section": testSection,
      style: styles,
      ...props,
      children: tableHeaderContent
    }
  );
};
TH.propTypes = {
  /** Content within the `Table.TH` component */
  children: PropTypes.node,
  /** CSS class names. */
  className: PropTypes.string,
  /** Number of columns that the cell should span */
  colSpan: PropTypes.number,
  /**
    Tell the cell to take up the least amount of width possible. This only
    works well if the table layout is `auto`, not `fixed`.
  */
  isCollapsed: PropTypes.bool,
  /** Right-align the cell if the contents are numerical */
  isNumerical: PropTypes.bool,
  /** Sorting options */
  sorting: PropTypes.shape({
    /** Indicate if this column should be sortable */
    canSort: PropTypes.bool,
    /** Function that handles sorting */
    handleSort: PropTypes.func,
    /** Is the table currently being sorted by this column */
    isActiveColumn: PropTypes.bool,
    /** Current order. One of 'asc' or 'desc' */
    order: PropTypes.oneOf([SORT_ORDERS.ASC, SORT_ORDERS.DESC])
  }),
  /** Hook for automated JavaScript tests */
  testSection: PropTypes.string,
  /** Text alignment */
  textAlign: PropTypes.oneOf(["center", "right", "left"]),
  /** A number with a unit that becomes the width of the `Table` cell */
  width: PropTypes.string
};
TH.defaultProps = {
  isCollapsed: false,
  isNumerical: false,
  sorting: { canSort: false }
};
TH.displayName = "Table.TH";

export { TH as default };
